import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Navigate, useParams } from 'react-router-dom';
import type { DateTime } from 'luxon';
import useFetch, { Status } from '../../../../../hooks/fetch.hook';
import Alert from '../../../../Shared/Alert';
import type { ITextFieldFormGroupState } from '../../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../../validation/validation-info';
import TextFieldFormGroup from '../../../../Shared/TextFieldFormGroup';
import { ValidatableComponent, ValidationHelper } from '../../../../../helpers/validation.helper';
import ValidationSummary from '../../../../Shared/ValidationSummary';
import type IValidatable from '../../../../Shared/validatable.interface';
import type { IServerValidationError } from '../../../../Shared/validatable.interface';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { CompetitionApiModel } from '../../../models/competition.api-model';
import Spinner from '../../../../Shared/Spinner';
import AntiforgeryService from '../../../../../services/anti-forgery.service';

function formatDateTimeForInput(dateTime: DateTime): string {
  // The input type datetime-local expects the date and time to be separated by a 'T' character
  return `${dateTime.toFormat('yyyy-MM-dd')}T${dateTime.toFormat('HH:mm')}`;
}

function PaEgnaBenCompetition() {
  // === PARAMS ===
  const { competitionId } = useParams();

  // === FETCH DATA ===
  const [{ status, data, httpResponseStatusCode }] = useFetch<CompetitionApiModel>(`/api/paegnaben/admin/competitions/${competitionId}`, !competitionId);

  // === STATE ===
  const [nameState, setNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [startTimeState, setStartTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [endTimeState, setEndTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [registrationStartTime, setRegistrationStartTime] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [registrationEndTime, setRegistrationEndTime] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [hideScoreboardStartTime, setHideScoreboardStartTime] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [hideScoreboardEndTime, setHideScoreboardEndTime] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg1StartTimeState, setLeg1StartTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg1EndTimeState, setLeg1EndTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg1QualificationLimitState, setLeg1QualificationLimitState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg2StartTimeState, setLeg2StartTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg2EndTimeState, setLeg2EndTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg2QualificationLimitState, setLeg2QualificationLimitState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg3StartTimeState, setLeg3StartTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg3EndTimeState, setLeg3EndTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg3QualificationLimitState, setLeg3QualificationLimitState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg4StartTimeState, setLeg4StartTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg4EndTimeState, setLeg4EndTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg4QualificationLimitState, setLeg4QualificationLimitState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg5StartTimeState, setLeg5StartTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg5EndTimeState, setLeg5EndTimeState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [leg5QualificationLimitState, setLeg5QualificationLimitState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [saveStatus, setSaveStatus] = useState<Status>(Status.notperformed);
  const [saveErrorCode, setSaveErrorCode] = useState<number | undefined>(undefined);

  // === EFFECTS ===
  useEffect(() => {
    if (data) {
      const competition = CompetitionApiModel.mapToBusinessModel(data);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNameState({ value: competition.name, validationInfo: new ValidationInfo() });
      setStartTimeState({ value: formatDateTimeForInput(competition.startTime), validationInfo: new ValidationInfo() });
      setEndTimeState({ value: formatDateTimeForInput(competition.endTime), validationInfo: new ValidationInfo() });
      setRegistrationStartTime({ value: formatDateTimeForInput(competition.registrationStartTime), validationInfo: new ValidationInfo() });
      setRegistrationEndTime({ value: formatDateTimeForInput(competition.registrationEndTime), validationInfo: new ValidationInfo() });
      setHideScoreboardStartTime({ value: formatDateTimeForInput(competition.hideScoreboardStartTime), validationInfo: new ValidationInfo() });
      setHideScoreboardEndTime({ value: formatDateTimeForInput(competition.hideScoreboardEndTime), validationInfo: new ValidationInfo() });
      setLeg1StartTimeState({ value: formatDateTimeForInput(competition.legs[0].startTime), validationInfo: new ValidationInfo() });
      setLeg1EndTimeState({ value: formatDateTimeForInput(competition.legs[0].endTime), validationInfo: new ValidationInfo() });
      setLeg1QualificationLimitState({ value: competition.legs[0].qualificationLimit.toString(), validationInfo: new ValidationInfo() });
      setLeg2StartTimeState({ value: formatDateTimeForInput(competition.legs[1].startTime), validationInfo: new ValidationInfo() });
      setLeg2EndTimeState({ value: formatDateTimeForInput(competition.legs[1].endTime), validationInfo: new ValidationInfo() });
      setLeg2QualificationLimitState({ value: competition.legs[1].qualificationLimit.toString(), validationInfo: new ValidationInfo() });
      setLeg3StartTimeState({ value: formatDateTimeForInput(competition.legs[2].startTime), validationInfo: new ValidationInfo() });
      setLeg3EndTimeState({ value: formatDateTimeForInput(competition.legs[2].endTime), validationInfo: new ValidationInfo() });
      setLeg3QualificationLimitState({ value: competition.legs[2].qualificationLimit.toString(), validationInfo: new ValidationInfo() });
      setLeg4StartTimeState({ value: formatDateTimeForInput(competition.legs[3].startTime), validationInfo: new ValidationInfo() });
      setLeg4EndTimeState({ value: formatDateTimeForInput(competition.legs[3].endTime), validationInfo: new ValidationInfo() });
      setLeg4QualificationLimitState({ value: competition.legs[3].qualificationLimit.toString(), validationInfo: new ValidationInfo() });
      setLeg5StartTimeState({ value: formatDateTimeForInput(competition.legs[4].startTime), validationInfo: new ValidationInfo() });
      setLeg5EndTimeState({ value: formatDateTimeForInput(competition.legs[4].endTime), validationInfo: new ValidationInfo() });
      setLeg5QualificationLimitState({ value: competition.legs[4].qualificationLimit.toString(), validationInfo: new ValidationInfo() });
    }
  }, [data]);

  // === REFS ===
  const nameRef = useRef<IValidatable>(null!);
  const startTimeRef = useRef<IValidatable>(null!);
  const endTimeRef = useRef<IValidatable>(null!);
  const registrationStartTimeRef = useRef<IValidatable>(null!);
  const registrationEndTimeRef = useRef<IValidatable>(null!);
  const hideScoreboardStartTimeRef = useRef<IValidatable>(null!);
  const hideScoreboardEndTimeRef = useRef<IValidatable>(null!);
  const leg1StartTimeRef = useRef<IValidatable>(null!);
  const leg1EndTimeRef = useRef<IValidatable>(null!);
  const leg1QualificationLimitRef = useRef<IValidatable>(null!);
  const leg2StartTimeRef = useRef<IValidatable>(null!);
  const leg2EndTimeRef = useRef<IValidatable>(null!);
  const leg2QualificationLimitRef = useRef<IValidatable>(null!);
  const leg3StartTimeRef = useRef<IValidatable>(null!);
  const leg3EndTimeRef = useRef<IValidatable>(null!);
  const leg3QualificationLimitRef = useRef<IValidatable>(null!);
  const leg4StartTimeRef = useRef<IValidatable>(null!);
  const leg4EndTimeRef = useRef<IValidatable>(null!);
  const leg4QualificationLimitRef = useRef<IValidatable>(null!);
  const leg5StartTimeRef = useRef<IValidatable>(null!);
  const leg5EndTimeRef = useRef<IValidatable>(null!);
  const leg5QualificationLimitRef = useRef<IValidatable>(null!);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: nameRef.current, validationInfo: nameState.validationInfo, setState: setNameState },
      { component: startTimeRef.current, validationInfo: startTimeState.validationInfo, setState: setStartTimeState },
      { component: endTimeRef.current, validationInfo: endTimeState.validationInfo, setState: setEndTimeState },
      { component: registrationStartTimeRef.current, validationInfo: registrationStartTime.validationInfo, setState: setRegistrationStartTime },
      { component: registrationEndTimeRef.current, validationInfo: registrationEndTime.validationInfo, setState: setRegistrationEndTime },
      { component: hideScoreboardStartTimeRef.current, validationInfo: hideScoreboardStartTime.validationInfo, setState: setHideScoreboardStartTime },
      { component: hideScoreboardEndTimeRef.current, validationInfo: hideScoreboardEndTime.validationInfo, setState: setHideScoreboardEndTime },
      { component: leg1StartTimeRef.current, validationInfo: leg1StartTimeState.validationInfo, setState: setLeg1StartTimeState },
      { component: leg1EndTimeRef.current, validationInfo: leg1EndTimeState.validationInfo, setState: setLeg1EndTimeState },
      { component: leg1QualificationLimitRef.current, validationInfo: leg1QualificationLimitState.validationInfo, setState: setLeg1QualificationLimitState },
      { component: leg2StartTimeRef.current, validationInfo: leg2StartTimeState.validationInfo, setState: setLeg2StartTimeState },
      { component: leg2EndTimeRef.current, validationInfo: leg2EndTimeState.validationInfo, setState: setLeg2EndTimeState },
      { component: leg2QualificationLimitRef.current, validationInfo: leg2QualificationLimitState.validationInfo, setState: setLeg2QualificationLimitState },
      { component: leg3StartTimeRef.current, validationInfo: leg3StartTimeState.validationInfo, setState: setLeg3StartTimeState },
      { component: leg3EndTimeRef.current, validationInfo: leg3EndTimeState.validationInfo, setState: setLeg3EndTimeState },
      { component: leg3QualificationLimitRef.current, validationInfo: leg3QualificationLimitState.validationInfo, setState: setLeg3QualificationLimitState },
      { component: leg4StartTimeRef.current, validationInfo: leg4StartTimeState.validationInfo, setState: setLeg4StartTimeState },
      { component: leg4EndTimeRef.current, validationInfo: leg4EndTimeState.validationInfo, setState: setLeg4EndTimeState },
      { component: leg4QualificationLimitRef.current, validationInfo: leg4QualificationLimitState.validationInfo, setState: setLeg4QualificationLimitState },
      { component: leg5StartTimeRef.current, validationInfo: leg5StartTimeState.validationInfo, setState: setLeg5StartTimeState },
      { component: leg5EndTimeRef.current, validationInfo: leg5EndTimeState.validationInfo, setState: setLeg5EndTimeState },
      { component: leg5QualificationLimitRef.current, validationInfo: leg5QualificationLimitState.validationInfo, setState: setLeg5QualificationLimitState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSaveButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();

    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (saveStatus === Status.loading) return;

      setSaveStatus(Status.notperformed);
      setSaveErrorCode(undefined);

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      setSaveStatus(Status.loading);
      const apiModel: CompetitionApiModel = {
        id: competitionId ? parseInt(competitionId, 10) : -1,
        name: nameState.value,
        startTime: startTimeState.value,
        endTime: endTimeState.value,
        registrationStartTime: registrationStartTime.value,
        registrationEndTime: registrationEndTime.value,
        hideScoreboardStartTime: hideScoreboardStartTime.value,
        hideScoreboardEndTime: hideScoreboardEndTime.value,
        legs: [
          {
            id: data ? data.legs[0].id : -1,
            number: 1,
            startTime: leg1StartTimeState.value,
            endTime: leg1EndTimeState.value,
            qualificationLimit: parseInt(leg1QualificationLimitState.value as string, 10),
          },
          {
            id: data ? data.legs[1].id : -1,
            number: 2,
            startTime: leg2StartTimeState.value,
            endTime: leg2EndTimeState.value,
            qualificationLimit: parseInt(leg2QualificationLimitState.value as string, 10),
          },
          {
            id: data ? data.legs[2].id : -1,
            number: 3,
            startTime: leg3StartTimeState.value,
            endTime: leg3EndTimeState.value,
            qualificationLimit: parseInt(leg3QualificationLimitState.value as string, 10),
          },
          {
            id: data ? data.legs[3].id : -1,
            number: 4,
            startTime: leg4StartTimeState.value,
            endTime: leg4EndTimeState.value,
            qualificationLimit: parseInt(leg4QualificationLimitState.value as string, 10),
          },
          {
            id: data ? data.legs[4].id : -1,
            number: 5,
            startTime: leg5StartTimeState.value,
            endTime: leg5EndTimeState.value,
            qualificationLimit: parseInt(leg5QualificationLimitState.value as string, 10),
          },
        ],
      };
      try {
        const response = await fetch(`/api/paegnaben/admin/competitions${competitionId ? `/${competitionId}` : ''}`, {
          method: competitionId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
          body: JSON.stringify(apiModel),
        });
        if (response.ok) {
          setSaveStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Tävling skapad', '');
        } else {
          setSaveStatus(Status.failed);
          setSaveErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          }
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenAdmin, 'Tävling skapad - misslyckat', '');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setSaveStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: error.status };
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('form-validation-summary')?.focus();
    }
  }

  // === RENDER ===
  if (status === Status.failed && httpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (saveStatus === Status.failed && saveErrorCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (status === Status.failed && httpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (saveStatus === Status.failed && saveErrorCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (saveStatus === Status.success) {
    return <Navigate to="/tavlingar" />;
  }

  return (
    <div>
      {competitionId ? <h1>Redigera tävling</h1> : <h1>Skapa ny tävling</h1>}
      {status === Status.loading && <Spinner className="my-4 my-md-5" />}
      {status === Status.failed && <Alert show={true} heading={`Kunde inte hämta tävlingen med id '${competitionId}'`} closable={false} />}
      {(!competitionId || status === Status.success) && (
        <form id="form">
          <div className="mb-4">
            <ValidationSummary id="form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
            <h2 className="visually-hidden">Generell information</h2>
            <div className="row">
              <TextFieldFormGroup ref={nameRef} labelText="Namn" required={true} inputId="name-input" className="col-md-4" serverValidationName="name" state={[nameState, setNameState]} />
            </div>
            <div className="row">
              <TextFieldFormGroup
                ref={startTimeRef}
                labelText="Tävlingens start"
                required={true}
                inputId="start-time-input"
                type="datetime-local"
                className="col-md-4"
                serverValidationName="startTime"
                state={[startTimeState, setStartTimeState]}
              />
              <TextFieldFormGroup
                ref={endTimeRef}
                labelText="Tävlingens slut"
                required={true}
                inputId="end-time-input"
                type="datetime-local"
                className="col-md-4"
                serverValidationName="endTime"
                state={[endTimeState, setEndTimeState]}
              />
            </div>
            <div className="row">
              <TextFieldFormGroup
                ref={registrationStartTimeRef}
                labelText="Anmälningstidens start"
                required={true}
                inputId="registration-start-time-input"
                type="datetime-local"
                className="col-md-4"
                serverValidationName="registrationStartTime"
                state={[registrationStartTime, setRegistrationStartTime]}
              />
              <TextFieldFormGroup
                ref={registrationEndTimeRef}
                labelText="Anmälningstidens slut"
                required={true}
                inputId="registration-end-time-input"
                type="datetime-local"
                className="col-md-4"
                serverValidationName="registrationEndTime"
                state={[registrationEndTime, setRegistrationEndTime]}
              />
            </div>
            <div className="row">
              <TextFieldFormGroup
                ref={hideScoreboardStartTimeRef}
                labelText="Dölj ställningen start"
                required={true}
                inputId="hide-scoreboard-start-time-input"
                type="datetime-local"
                className="col-md-4"
                serverValidationName="hideScoreboardStartTime"
                state={[hideScoreboardStartTime, setHideScoreboardStartTime]}
              />
              <TextFieldFormGroup
                ref={hideScoreboardEndTimeRef}
                labelText="Dölj ställningen slut"
                required={true}
                inputId="hide-scoreboard-end-time-input"
                type="datetime-local"
                className="col-md-4 mb-0"
                serverValidationName="hideScoreboardEndTime"
                state={[hideScoreboardEndTime, setHideScoreboardEndTime]}
              />
            </div>
          </div>
          <div className="mb-4">
            <h2 className="visually-hidden">Etapper</h2>
            <div>
              <h3>Etapp 1</h3>
              <div className="row">
                <TextFieldFormGroup
                  ref={leg1StartTimeRef}
                  labelText="Starttid"
                  required={true}
                  inputId="leg-1-start-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg1StartTime"
                  state={[leg1StartTimeState, setLeg1StartTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg1EndTimeRef}
                  labelText="Sluttid"
                  required={true}
                  inputId="leg-1-end-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg1EndTime"
                  state={[leg1EndTimeState, setLeg1EndTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg1QualificationLimitRef}
                  labelText="Kvalgräns"
                  required={true}
                  inputId="leg-1-qualification-limit-input"
                  type="number"
                  className="col-md-4"
                  serverValidationName="leg1QualificationLimit"
                  state={[leg1QualificationLimitState, setLeg1QualificationLimitState]}
                />
              </div>
            </div>
            <div>
              <h3>Etapp 2</h3>
              <div className="row">
                <TextFieldFormGroup
                  ref={leg2StartTimeRef}
                  labelText="Starttid"
                  required={true}
                  inputId="leg-2-start-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg2StartTime"
                  state={[leg2StartTimeState, setLeg2StartTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg2EndTimeRef}
                  labelText="Sluttid"
                  required={true}
                  inputId="leg-2-end-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg2EndTime"
                  state={[leg2EndTimeState, setLeg2EndTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg2QualificationLimitRef}
                  labelText="Kvalgräns"
                  required={true}
                  inputId="leg-2-qualification-limit-input"
                  type="number"
                  className="col-md-4"
                  serverValidationName="leg2QualificationLimit"
                  state={[leg2QualificationLimitState, setLeg2QualificationLimitState]}
                />
              </div>
            </div>
            <div>
              <h3>Etapp 3</h3>
              <div className="row">
                <TextFieldFormGroup
                  ref={leg3StartTimeRef}
                  labelText="Starttid"
                  required={true}
                  inputId="leg-3-start-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg3StartTime"
                  state={[leg3StartTimeState, setLeg3StartTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg3EndTimeRef}
                  labelText="Sluttid"
                  required={true}
                  inputId="leg-3-end-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg3EndTime"
                  state={[leg3EndTimeState, setLeg3EndTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg3QualificationLimitRef}
                  labelText="Kvalgräns"
                  required={true}
                  inputId="leg-3-qualification-limit-input"
                  type="number"
                  className="col-md-4"
                  serverValidationName="leg3QualificationLimit"
                  state={[leg3QualificationLimitState, setLeg3QualificationLimitState]}
                />
              </div>
            </div>
            <div>
              <h3>Etapp 4</h3>
              <div className="row">
                <TextFieldFormGroup
                  ref={leg4StartTimeRef}
                  labelText="Starttid"
                  required={true}
                  inputId="leg-4-start-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg4StartTime"
                  state={[leg4StartTimeState, setLeg4StartTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg4EndTimeRef}
                  labelText="Sluttid"
                  required={true}
                  inputId="leg-4-end-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg4EndTime"
                  state={[leg4EndTimeState, setLeg4EndTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg4QualificationLimitRef}
                  labelText="Kvalgräns"
                  required={true}
                  inputId="leg-4-qualification-limit-input"
                  type="number"
                  className="col-md-4"
                  serverValidationName="leg4QualificationLimit"
                  state={[leg4QualificationLimitState, setLeg4QualificationLimitState]}
                />
              </div>
            </div>
            <div>
              <h3>Etapp 5</h3>
              <div className="row">
                <TextFieldFormGroup
                  ref={leg5StartTimeRef}
                  labelText="Starttid"
                  required={true}
                  inputId="leg-5-start-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg5StartTime"
                  state={[leg5StartTimeState, setLeg5StartTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg5EndTimeRef}
                  labelText="Sluttid"
                  required={true}
                  inputId="leg-5-end-time-input"
                  type="datetime-local"
                  className="col-md-4"
                  serverValidationName="leg5EndTime"
                  state={[leg5EndTimeState, setLeg5EndTimeState]}
                />
                <TextFieldFormGroup
                  ref={leg5QualificationLimitRef}
                  labelText="Kvalgräns"
                  required={true}
                  inputId="leg-5-qualification-limit-input"
                  type="number"
                  className="col-md-4"
                  serverValidationName="leg5QualificationLimit"
                  state={[leg5QualificationLimitState, setLeg5QualificationLimitState]}
                />
              </div>
            </div>
          </div>
          <button type="button" className="btn btn-primary" onClick={handleSaveButtonClick}>
            Spara tävling
          </button>
          {saveStatus === Status.failed && <Alert show={true} heading="Kunde inte spara tävling" closable={false} className="mt-3" />}
        </form>
      )}
    </div>
  );
}

export default PaEgnaBenCompetition;